import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useAuth, type User } from "../store/auth";
import { Logo } from "../components/Logo";

export function LoginPage() {
  const nav = useNavigate();
  const setUser = useAuth((s) => s.setUser);
  const flags = useQuery({
    queryKey: ["flags"],
    queryFn: () => api<Record<string, boolean>>("/flags"),
  });
  const demo = flags.data?.DEMO_MODE === true;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  return (
    <div className="wrap auth-box">
      <div className="auth-brand"><Logo markSize={44} /></div>
      <h1>Sign in</h1>
      {demo && <p>Development demo: demo@softwave.markets / demo1234.</p>}
      <form onSubmit={async (e) => {
        e.preventDefault();
        setErr("");
        try {
          const d = await api<{ user: User }>("/auth/login", { method: "POST", body: JSON.stringify({ email, password }) });
          setUser(d.user);
          nav("/");
        } catch (ex) {
          setErr((ex as Error).message);
        }
      }} className="card">
        <label className="field">Email<input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required autoComplete="username" /></label>
        <label className="field">Password<input value={password} onChange={(e) => setPassword(e.target.value)} type="password" required autoComplete="current-password" /></label>
        {err && <p style={{ color: "var(--danger)" }}>{err}</p>}
        <button className="btn btn-primary btn-block">Sign in</button>
      </form>
      <p><Link to="/register">Create an account</Link></p>
    </div>
  );
}

export function RegisterPage() {
  const nav = useNavigate();
  const setUser = useAuth((s) => s.setUser);
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  return (
    <div className="wrap auth-box">
      <div className="auth-brand"><Logo markSize={44} /></div>
      <h1>Create account</h1>
      <p>New accounts start unverified. You can trade after you sign in. Withdrawals wait until an admin completes KYC.</p>
      <form onSubmit={async (e) => {
        e.preventDefault();
        setErr("");
        try {
          const d = await api<{ user: User }>("/auth/register", { method: "POST", body: JSON.stringify({ email, username, password }) });
          setUser(d.user);
          nav("/");
        } catch (ex) {
          setErr((ex as Error).message);
        }
      }} className="card">
        <label className="field">Email<input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required /></label>
        <label className="field">Username<input value={username} onChange={(e) => setUsername(e.target.value)} required /></label>
        <label className="field">Password<input value={password} onChange={(e) => setPassword(e.target.value)} type="password" minLength={8} required /></label>
        {err && <p style={{ color: "var(--danger)" }}>{err}</p>}
        <button className="btn btn-primary btn-block">Create account</button>
      </form>
    </div>
  );
}

export function HowItWorks() {
  return (
    <div className="wrap prose">
      <h1>How Softwave Markets works</h1>
      <div className="onboard-steps">
        <div><strong>1. Find a question</strong> that can be resolved from a public source.</div>
        <div><strong>2. Choose YES or NO.</strong> A YES price of 64¢ is the market’s ~64% probability.</div>
        <div><strong>3. Buy contracts</strong> from another trader — or rest a limit if the book is thin.</div>
        <div><strong>4. Trade out or wait.</strong> You can sell before resolution if someone will buy.</div>
        <div><strong>5. Settlement.</strong> Winning contracts redeem at $1.00 from reserved collateral. Softwave does not pay winners out of a house bankroll.</div>
      </div>
      <p>Example: Bitcoin YES at 64¢. You buy 100 YES for about $64 plus the listed fee. If YES resolves, those 100 contracts pay $100. If you sell at 70¢ first, you lock in the move minus fees.</p>
    </div>
  );
}
